/**
 * Play Together — scripted micro-simulations for each quick-reply path (EN/ES).
 * Maps: literacy → Animal ABCs | breathing → Pepe/tree | draw → Superpowers | express → Peke
 */

import type { Language } from '@/lib/voiceManager';
import type { HomeGameScreen } from './homeGameCards';
import { getPlayTogetherPostSimHandoff } from './playTogetherMicroOnboarding';
import {
  getLiteracyBeat0Frame,
  getLiteracyBeat1Skills,
  getLiteracyBeat2Caregiver,
  getLiteracyBeat3Feedback,
  getLiteracyBeat4Readiness,
  getLiteracyAfterCaregiverWrapUp,
  getLiteracyConstructiveCaregiverFeedback,
  getLiteracyConstructiveCaregiverFeedbackRound2,
  getLiteracyCaregiverSoftLanding,
  assessLiteracyCaregiverResponse,
} from './earlyLiteracyPlaySimulation';

export type GameSimKind = 'literacy' | 'breathing' | 'draw' | 'express';

/** Micro-sim focus → homepage tile to highlight after practice (matches quick-reply themes). */
export const GAME_SIM_KIND_TO_HOME_SCREEN: Record<GameSimKind, HomeGameScreen> = {
  literacy: 'animal-abcs',
  breathing: 'tree-level',
  draw: 'creature-superpowers',
  express: 'peke-level',
};

/** Quick-reply index 0–3 → simulation kind */
export const QUICK_REPLY_SIM_KIND: readonly GameSimKind[] = [
  'literacy',
  'breathing',
  'draw',
  'express',
] as const;

export type PlaySimStep =
  | 'await_animal_choice'
  | 'await_first_input'
  | 'await_skill_choice'
  | 'await_caregiver'
  | 'await_skill_describe'
  | 'await_skill_reflect'
  | 'await_skill_praise'
  | 'await_skill_relate'
  | 'await_skill_care'
  | 'complete';

/** Animal character the user plays as during the child role */
export interface AnimalCharacter {
  id: string;
  nameEn: string;
  nameEs: string;
  emoji: string;
  traitEn: string;
  traitEs: string;
}

/** Available animal characters for role-play */
export const ANIMAL_CHARACTERS: readonly AnimalCharacter[] = [
  { id: 'rabbit', nameEn: 'Rabbit', nameEs: 'Conejo', emoji: '🐰', traitEn: 'curious and gentle', traitEs: 'curioso y gentil' },
  { id: 'fox', nameEn: 'Fox', nameEs: 'Zorro', emoji: '🦊', traitEn: 'clever and playful', traitEs: 'listo y juguetón' },
  { id: 'owl', nameEn: 'Owl', nameEs: 'Búho', emoji: '🦉', traitEn: 'wise and observant', traitEs: 'sabio y observador' },
  { id: 'deer', nameEn: 'Deer', nameEs: 'Venado', emoji: '🦌', traitEn: 'graceful and calm', traitEs: 'grácil y tranquilo' },
  { id: 'squirrel', nameEn: 'Squirrel', nameEs: 'Ardilla', emoji: '🐿️', traitEn: 'energetic and creative', traitEs: 'enérgico y creativo' },
  { id: 'bird', nameEn: 'Bird', nameEs: 'Pájaro', emoji: '🐦', traitEn: 'free and imaginative', traitEs: 'libre e imaginativo' },
] as const;

export { literacySkillChoiceLabels } from './earlyLiteracyPlaySimulation';

/** How many coaching assistant messages we show before soft-landing into Beats 3–4 */
export const PLAY_SIM_MAX_COACH_MESSAGES = 2;

/** Skill visual and audio design tokens */
export const SKILL_DESIGN_TOKENS = {
  describe: {
    id: 'describe',
    nameEn: 'Observe & Describe',
    nameEs: 'Observar y Describir',
    emoji: '👀',
    color: '#A8D5E2', // soft sky blue
    soundFreq: 523.25, // C5
  },
  reflect: {
    id: 'reflect',
    nameEn: 'Connect & Reflect',
    nameEs: 'Conectar y Reflejar',
    emoji: '🤝',
    color: '#D4C5F9', // soft lavender
    soundFreq: 587.33, // D5
  },
  praise: {
    id: 'praise',
    nameEn: 'Compliment & Praise',
    nameEs: 'Elogiar y Valorar',
    emoji: '✨',
    color: '#FFB6C1', // soft pink/coral
    soundFreq: 659.25, // E5
  },
  relate: {
    id: 'relate',
    nameEn: 'Associate & Relate',
    nameEs: 'Asociar y Relacionar',
    emoji: '🌿',
    color: '#FFD4A3', // soft peach
    soundFreq: 698.46, // F5
  },
  care: {
    id: 'care',
    nameEn: 'Care for All Life',
    nameEs: 'Cuida toda la vida',
    emoji: '💚',
    color: '#B8E6B8', // soft mint green
    soundFreq: 783.99, // G5
  },
} as const;

/** Get skill design token by step */
export function getSkillDesignToken(
  skillStep: 'await_skill_describe' | 'await_skill_reflect' | 'await_skill_praise' | 'await_skill_relate' | 'await_skill_care'
): typeof SKILL_DESIGN_TOKENS[keyof typeof SKILL_DESIGN_TOKENS] {
  const skillKey = skillStep.replace('await_skill_', '') as keyof typeof SKILL_DESIGN_TOKENS;
  return SKILL_DESIGN_TOKENS[skillKey];
}

/** Draw / Creature Superpowers — same demonstration pattern as literacy (four lines + parenthetical skill notes). */
function getDrawBeat1Demonstration(w: string, lang: Language): string {
  return lang === 'en'
    ? [
        'As the caregiver using the **four skills** and **sustainability learning**, I would say:',
        '',
        `You imagined **${w}** (Describe skill: name back their creature, powers, and what makes it exciting)`,
        '',
        `I'm right there with that energy—**${w}** stays in the room with us (Reflect skill: mirror the child's play energy)`,
        '',
        'I’m glad you said it out loud before the first mark! (Praise skill: highlight what the child did in a positive way)',
        '',
        `Let's draw **${w}** on paper we already have—we can **reuse** the edges or back of old drawings. Taking care of our materials helps take care of our Earth. (Relate skill: add a connecting thought about caring for the world through how we play)`,
        '',
        "**What else can this creature do?** I wonder where it lives? **You lead**—I'll keep imagining with you. (Extend imagination: use open-ended questions and comments to fuel more creativity and keep the child leading)",
        '',
        'Which line felt strongest—or the **whole demonstration** together? (Tap a button or type.)',
      ].join('\n')
    : [
        'Como cuidador/a usando las **cuatro habilidades** y el **aprendizaje en sostenibilidad**, diría:',
        '',
        `Imaginaste **${w}** (habilidad Describir: devolver criatura, poderes y lo que la hace emocionante)`,
        '',
        `Estoy contigo con esa energía—**${w}** se queda aquí con nosotros (habilidad Reflejar: espejar la energía de juego del niño/a)`,
        '',
        'Me alegra que lo dijeras en voz alta antes del primer trazo. (habilidad Elogiar: destacar en positivo lo que hizo el niño/a)',
        '',
        `Dibujemos **${w}** en papel que ya tenemos—podemos **reutilizar** los bordes o la parte de atrás de dibujos viejos. Cuidar nuestros materiales ayuda a cuidar nuestra Tierra. (habilidad Relacionar: añade un pensamiento sobre cuidar el mundo a través de cómo jugamos)`,
        '',
        '**¿Qué más puede hacer esta criatura?** Me pregunto dónde vive. **Tú guías**—yo sigo imaginando contigo. (Extender imaginación: usa preguntas y comentarios abiertos para alimentar más creatividad y mantener al niño/a liderando)',
        '',
        '¿Qué frase te resonó más, o la **demostración entera**? (Toca un botón o escribe.)',
      ].join('\n');
}

/**
 * Kind-aware caregiver reply quality (literacy uses Animal ABCs heuristics; other paths use themed cues).
 */
export function assessCaregiverResponseForKind(
  kind: GameSimKind,
  text: string,
  lang: Language
): { level: 'weak' | 'ok' | 'strong' } {
  if (kind === 'literacy') return assessLiteracyCaregiverResponse(text, lang);
  return assessNonLiteracyCaregiverResponse(kind, text, lang);
}

function assessNonLiteracyCaregiverResponse(
  kind: Exclude<GameSimKind, 'literacy'>,
  raw: string,
  lang: Language
): { level: 'weak' | 'ok' | 'strong' } {
  const trimmed = raw.trim();
  if (trimmed.length < 22) return { level: 'weak' };

  const t = trimmed.toLowerCase();
  const isEn = lang === 'en';

  const describe = isEn
    ? /\b(i see|you('re|r)|your|notice|noticed|hear|heard|saying|look|looking|whisper|words|leaves|breeze|peaceful|breathing|sunlight|branches|quiet|rustling|spark|zigzag|drawing|hop|excited|bunny)\b/i.test(t)
    : /\b(veo|noto|notas|escucho|escuchas|dices|susurro|palabras|hojas|brisa|paz|respir|tranquil|luz|ramas|chispa|dibujo|saltar|emocionad)\b/i.test(t);

  const reflect = isEn
    ? /\b(like|same|together|me too|with you|feel|sounds|that with|i hear)\b/i.test(t)
    : /\b(como|igual|juntos|contigo|también|siento|escucho|eso)\b/i.test(t);

  const praise = isEn
    ? /\b(love|great|good|nice|brave|clever|proud|happy|wow|yes|brave|honest|noticing)\b/i.test(t)
    : /\b(me encanta|genial|bien|buen|valiente|orgullo|feliz|sí|honest)\b/i.test(t);

  const relate = isEn
    ? /\b(remind|remember|also|maybe|home|kind|care|family|earth|trees|paper|reuse|garden|weather|place)\b/i.test(t)
    : /\b(recuerda|además|también|casa|bondad|cuidar|familia|tierra|árbol|papel|reutil|jardín|clima|lugar)\b/i.test(t);

  let theme = false;
  if (kind === 'breathing') {
    theme = isEn
      ? /\b(breath|breathe|air|tree|trees|leaf|leaves|wind|calm|body|nature|earth|outside|planet|care)\b/i.test(t)
      : /\b(respir|aire|árbol|árboles|hoja|viento|calma|cuerpo|naturaleza|tierra|planeta|cuidar)\b/i.test(t);
  } else if (kind === 'draw') {
    theme = isEn
      ? /\b(draw|line|mark|paper|sky|lightning|reuse|scrap|leaf|nature|color|creative|page)\b/i.test(t)
      : /\b(dibuj|trazo|papel|cielo|rayo|reutil|hoja|naturaleza|color|creativ|página)\b/i.test(t);
  } else {
    theme = isEn
      ? /\b(feeling|feel|excited|family|home|kind|heart|listen|care|garden|weather|others)\b/i.test(t)
      : /\b(sentim|emoci|familia|hogar|bondad|corazón|escuch|cuidar|jardín|clima|demás)\b/i.test(t);
  }

  let score = 0;
  if (describe) score += 1;
  if (reflect) score += 1;
  if (praise) score += 1;
  if (relate) score += 1;
  if (theme) score += 1;

  if (score >= 4 && trimmed.length >= 35) return { level: 'strong' };
  if (score >= 3 && trimmed.length >= 30) return { level: 'ok' };
  if (score <= 2 || trimmed.length < 28) return { level: 'weak' };
  if (!theme && score <= 3 && trimmed.length < 55) return { level: 'weak' };
  return { level: 'ok' };
}

/** Plain lead-in before focus-specific Beat 0 (after user picks a quick-reply focus). */
export function getSimulationBeat0Intro(lang: Language): string {
  return lang === 'en'
    ? 'We will play a brief play simulation using the Wonderpath approach for sustainable, creative, expressive play.'
    : 'Jugaremos una breve simulación de juego con el enfoque Wonderpath: juego sostenible, creativo y expresivo.';
}

/** Animal character selection prompt */
export function getAnimalSelectionPrompt(lang: Language): string {
  return lang === 'en'
    ? [
        '**First, let\'s pick your animal character!**',
        '',
        'You\'ll play as this animal when you\'re the child in our practice.',
        '',
        '**Choose an animal** below:',
      ].join('\n')
    : [
        '**¡Primero, elige tu personaje animal!**',
        '',
        'Jugarás como este animal cuando seas el niño o la niña en nuestra práctica.',
        '',
        '**Elige un animal** abajo:',
      ].join('\n');
}

/** First input prompt with animal character introduction */
export function getFirstInputPromptWithAnimal(
  kind: GameSimKind,
  lang: Language,
  animal: AnimalCharacter
): string {
  const animalIntro =
    lang === 'en'
      ? `**Great! You're a ${animal.traitEn} ${animal.emoji} ${animal.nameEn}.**`
      : `**¡Genial! Eres un ${animal.emoji} ${animal.nameEs} ${animal.traitEs}.**`;

  if (kind === 'literacy') {
    // Literacy has its own flow - handle separately if needed
    return lang === 'en'
      ? `${animalIntro}\n\nNow, **type or say** a **letter** (like A, B, or C).`
      : `${animalIntro}\n\nAhora, **escribe o di** una **letra** (como A, B o C).`;
  }

  if (kind === 'breathing') {
    return lang === 'en'
      ? `${animalIntro}\n\n**Type or say** something you notice **outdoors**, and add **how it feels in your body** (for example: *cloud shapes*, *wind loud in the branches*, *sun warm on my arms*).`
      : `${animalIntro}\n\n**Escribe o di** algo que notes **afuera**, y añade **cómo lo sientes en el cuerpo** (p. ej.: *formas de nubes*, *viento fuerte en las ramas*, *el sol calentándome los brazos*).`;
  }

  if (kind === 'draw') {
    return lang === 'en'
      ? `${animalIntro}\n\n**Type or say** something you'd like to draw or imagine (for example: a creature with the **superpower** to spark **blue-colored energy** and **fly like a falcon**).`
      : `${animalIntro}\n\n**Escribe o di** algo que te gustaría dibujar o imaginar (p. ej.: una **criatura** con el **superpoder** de lanzar **energía azul chispeante** y **volar como un halcón**).`;
  }

  // express (Peke)
  return lang === 'en'
    ? `${animalIntro}\n\n**Type or say** what you notice about how **Peke** is feeling (for example: *Peke is sleeping*, *Peke looks sad*, *Peke is happy and laughing*).`
    : `${animalIntro}\n\n**Escribe o di** lo que notas sobre cómo **se siente Peke** (p. ej.: *Peke está durmiendo*, *Peke se ve triste*, *Peke está feliz y riéndose*).`;
}

/** Conversational prompts for each skill in the caregiver response building */
export function getConversationalSkillPrompt(
  skillStep: 'await_skill_describe' | 'await_skill_reflect' | 'await_skill_praise' | 'await_skill_relate' | 'await_skill_care',
  lang: Language,
  childObservation: string
): string {
  const child = childObservation.trim() || (lang === 'en' ? 'what the child shared' : 'lo que compartió el niño/a');

  if (skillStep === 'await_skill_describe') {
    return lang === 'en'
      ? [
          `**Let's build your response piece by piece.**`,
          '',
          `The child said: **"${child}"**`,
          '',
          '**👀 Observe & Describe**',
          '',
          'What did the child notice? Name it simply, without adding or fixing.',
          '',
          '**Type or say** your observation:',
        ].join('\n')
      : [
          `**Construyamos tu respuesta paso a paso.**`,
          '',
          `El niño/a dijo: **"${child}"**`,
          '',
          '**👀 Observar y Describir**',
          '',
          'Qué notó el niño/a? Nómbralo simplemente, sin añadir ni corregir.',
          '',
          '**Escribe o di** tu observación:',
        ].join('\n');
  }

  if (skillStep === 'await_skill_reflect') {
    return lang === 'en'
      ? [
          '**🤝 Connect & Reflect**',
          '',
          'Stay with what they shared. Mirror their energy, words, or discovery.',
          '',
          '**Type or say** how you reflect back:',
        ].join('\n')
      : [
          '**🤝 Conectar y Reflejar**',
          '',
          'Quédate con lo que compartieron. Refleja su energía, palabras o descubrimiento.',
          '',
          '**Escribe o di** cómo reflejas:',
        ].join('\n');
  }

  if (skillStep === 'await_skill_praise') {
    return lang === 'en'
      ? [
          '**✨ Compliment & Praise**',
          '',
          'Name something you genuinely appreciate—their curiosity, their try, their noticing.',
          '',
          '**Type or say** what you appreciate:',
        ].join('\n')
      : [
          '**✨ Elogiar y Valorar**',
          '',
          'Nombra algo que genuinamente aprecias—su curiosidad, su intento, su observación.',
          '',
          '**Escribe o di** lo que valoras:',
        ].join('\n');
  }

  if (skillStep === 'await_skill_relate') {
    return lang === 'en'
      ? [
          '**🌿 Associate & Relate**',
          '',
          'Connect to something bigger—wonder, nature, each other.',
          '',
          '**Type or say** how this connects:',
        ].join('\n')
      : [
          '**🌿 Asociar y Relacionar**',
          '',
          'Conecta con algo más grande—asombro, naturaleza, los demás.',
          '',
          '**Escribe o di** cómo se conecta:',
        ].join('\n');
  }

  // await_skill_care
  return lang === 'en'
    ? [
        '**💚 Care for All Life**',
        '',
        'When we care, everything thrives. How does this moment show care?',
        '',
        '**Type or say** how care appears here:',
      ].join('\n')
    : [
        '**💚 Cuida toda la vida**',
        '',
        'Cuando cuidamos, todo florece. ¿Cómo este momento muestra cuidado?',
        '',
        '**Escribe o di** cómo aparece el cuidado:',
      ].join('\n');
}

/** Acknowledgment after each skill response before moving to next */
export function getSkillAcknowledgment(
  skillStep: 'await_skill_describe' | 'await_skill_reflect' | 'await_skill_praise' | 'await_skill_relate' | 'await_skill_care',
  lang: Language,
  userResponse: string
): string {
  const response = userResponse.trim();

  if (skillStep === 'await_skill_describe') {
    return lang === 'en'
      ? `**Nice.** You observed: *"${response}"*`
      : `**Bien.** Observaste: *"${response}"*`;
  }

  if (skillStep === 'await_skill_reflect') {
    return lang === 'en'
      ? `**Good.** You reflected: *"${response}"*`
      : `**Bueno.** Reflejaste: *"${response}"*`;
  }

  if (skillStep === 'await_skill_praise') {
    return lang === 'en'
      ? `**Great.** You appreciated: *"${response}"*`
      : `**Genial.** Valoraste: *"${response}"*`;
  }

  if (skillStep === 'await_skill_relate') {
    return lang === 'en'
      ? `**Beautiful.** You connected: *"${response}"*`
      : `**Hermoso.** Conectaste: *"${response}"*`;
  }

  // await_skill_care
  return lang === 'en'
    ? `**Wonderful.** You showed care: *"${response}"*`
    : `**Maravilloso.** Mostraste cuidado: *"${response}"*`;
}

/** Opening frame (Beat 0) */
export function getSimulationBeat0(kind: GameSimKind, lang: Language): string {
  const intro = getSimulationBeat0Intro(lang);

  if (kind === 'literacy') {
    return `${intro}\n\n${getLiteracyBeat0Frame(lang)}`;
  }

  if (kind === 'breathing') {
    const body =
      lang === 'en'
        ? [
            "We'll do a **short play simulation** for **breathing mindfulness** with **Pepe** and the **tree**.",
            '',
            '**First**, you’ll play the **child**; **then**, the **caregiver**.',
            '',
            '**Type or say** something you notice **outdoors**, and add **how it feels in your body** (for example: *cloud shapes*, *wind loud in the branches*, *sun warm on my arms*).',
          ].join('\n')
        : [
            'Haremos una **simulación breve** de **atención plena y respiración** con **Pepe** y el **árbol**.',
            '',
            '**Primero** serás el **niño** o la **niña**; **después**, el **cuidador** o la **cuidadora**.',
            '',
            '**Escribe o di** algo que notes **afuera**, y añade **cómo lo sientes en el cuerpo** (p. ej.: *formas de nubes*, *viento fuerte en las ramas*, *el sol calentándome los brazos*).',
          ].join('\n');
    return `${intro}\n\n${body}`;
  }

  if (kind === 'draw') {
    const body =
      lang === 'en'
        ? [
            "We'll do a **short play simulation** for **drawing imagination** with **Creature Superpowers**.",
            '',
            '**First**, you’ll play the **child**; **then**, the **caregiver**.',
            '',
            '**Type or say** something you’d like to draw or imagine (for example: a creature with the **superpower** to spark **blue-colored energy** and **fly like a falcon**).',
          ].join('\n')
        : [
            'Haremos una **simulación breve** de **imaginación y dibujo** con **Superpoderes**.',
            '',
            '**Primero** harás de **niño o niña**; **después**, de **cuidador o cuidadora**.',
            '',
            '**Escribe o di** algo que te gustaría dibujar o imaginar (p. ej.: una **criatura** con el **superpoder** de lanzar **energía azul chispeante** y **volar como un halcón**).',
          ].join('\n');
    return `${intro}\n\n${body}`;
  }

  const expressBody =
    lang === 'en'
      ? [
          "**Peke** is our storytelling character for **expression and imagination**—a little friend whose feelings and adventures help children notice emotions, name them, and explore moments through stories.",
          '',
          "We'll do a **short play simulation** for **noticing and naming feelings** with **Peke's Adventures**.",
          '',
          '**First**, **child**; **then**, **caregiver**.',
          '',
          '**Type or say** what you notice about how **Peke** is feeling (for example: *Peke is sleeping*, *Peke looks sad*, *Peke is happy and laughing*).',
        ].join('\n')
      : [
          '**Peke** es nuestro personaje narrativo para **expresión e imaginación**—un amiguito cuyos sentimientos y aventuras ayudan a los niños a notar emociones, nombrarlas y explorar momentos a través de historias.',
          '',
          'Haremos una **simulación breve** para **notar y nombrar sentimientos** con las **Aventuras de Peke**.',
          '',
          '**Primero** **niño/a**; **después** **cuidador/a**.',
          '',
          '**Escribe o di** lo que notas sobre cómo **se siente Peke** (p. ej.: *Peke está durmiendo*, *Peke se ve triste*, *Peke está feliz y riéndose*).',
        ].join('\n');
  return `${intro}\n\n${expressBody}`;
}

/** After first user input — model 4 skills + skill question */
export function getSimulationBeat1Skills(kind: GameSimKind, lang: Language, raw: string): string {
  if (kind === 'literacy') return getLiteracyBeat1Skills(lang, raw);

  const w = raw.trim() || (lang === 'en' ? 'what you shared' : 'lo que compartiste');

  if (kind === 'breathing') {
    return lang === 'en'
      ? [
          'As the caregiver using the **four skills** and **sustainability learning**, I would say:',
          '',
          `You shared **${w}** (Describe skill: name what they noticed outside and in the body)`,
          '',
          `That lands—**${w}** (Reflect skill: mirror the child's calm, wonder, or energy)`,
          '',
          "I'm glad you paused to notice that. (Praise skill: highlight what the child did in a positive way)",
          '',
          "When we pause to notice the trees, wind, and air, we're caring for the living world around us—not rushing past it. (Relate skill: add a connecting thought about caring for nature through attention)",
          '',
          'Which line felt strongest—or the **whole demonstration** together? (Tap a button or type.)',
        ].join('\n')
      : [
          'Como cuidador/a usando las **cuatro habilidades** y el **aprendizaje en sostenibilidad**, diría:',
          '',
          `Compartiste **${w}** (habilidad Describir: nombrar lo que notaste afuera y en el cuerpo)`,
          '',
          `Así se siente—**${w}** (habilidad Reflejar: espejar la calma, el asombro o la energía del niño/a)`,
          '',
          'Me alegra que te hayas detenido a notarlo. (habilidad Elogiar: destacar en positivo lo que hizo el niño/a)',
          '',
          'Cuando nos detenemos a notar los árboles, el viento y el aire, estamos cuidando el mundo vivo a nuestro alrededor—sin pasar de largo. (habilidad Relacionar: añade un pensamiento sobre cuidar la naturaleza a través de la atención)',
          '',
          '¿Qué frase te resonó más, o la **demostración entera**? (Toca un botón o escribe.)',
        ].join('\n');
  }

  if (kind === 'draw') {
    return getDrawBeat1Demonstration(w, lang);
  }

  // express (Peke) — observing and naming Peke's feelings
  return lang === 'en'
    ? [
        'As the caregiver using the **four skills** and **sustainability learning**, I would say:',
        '',
        `You noticed **${w}** (Describe skill: name what they observed about Peke's feeling)`,
        '',
        `Yes—**${w}**. You're reading Peke's moment. (Reflect skill: mirror the child's observation and curiosity)`,
        '',
        "I love that you're noticing how Peke feels. That's paying attention to others. (Praise skill: highlight the skill of emotional noticing)",
        '',
        "When we notice how others feel—people, animals, even characters—we're practicing kindness and care for others. (Relate skill: add a connecting thought about caring for feelings as part of caring for our community)",
        '',
        "You know what? Sometimes **I feel happy like Peke too**—like today when we're playing together. And sometimes I feel sad, just like Peke does. **We all have different feelings**, and that's part of being human. It's okay to have all sorts of feelings. (Relate to ourselves: model talking about our own feelings using Peke's story as a bridge)",
        '',
        'Which line felt strongest—or the **whole demonstration** together? (Tap a button or type.)',
      ].join('\n')
    : [
        'Como cuidador/a usando las **cuatro habilidades** y el **aprendizaje en sostenibilidad**, diría:',
        '',
        `Notaste **${w}** (habilidad Describir: nombrar lo que observaron sobre el sentimiento de Peke)`,
        '',
        `Sí—**${w}**. Estás leyendo el momento de Peke. (habilidad Reflejar: espejar la observación y curiosidad del niño/a)`,
        '',
        'Me encanta que notes cómo se siente Peke. Eso es prestar atención a los demás. (habilidad Elogiar: destacar la habilidad de notar emociones)',
        '',
        'Cuando notamos cómo se sienten los demás—personas, animales, hasta personajes—estamos practicando bondad y cuidado por los demás. (habilidad Relacionar: añade un pensamiento sobre cuidar sentimientos como parte de cuidar nuestra comunidad)',
        '',
        '¿Sabes qué? A veces **yo también me siento feliz como Peke**—como hoy cuando jugamos juntos/as. Y a veces me siento triste, como Peke. **Todos tenemos diferentes sentimientos**, y eso es parte de ser humano/a. Está bien tener todo tipo de sentimientos. (Relacionar con nosotros/as mismos/as: modelar hablar sobre nuestros propios sentimientos usando la historia de Peke como puente)',
        '',
        '¿Qué frase te resonó más, o la **demostración entera**? (Toca un botón o escribe.)',
      ].join('\n');
}

/** Caregiver round — WonderGuidance plays child */
export function getSimulationBeat2Caregiver(kind: GameSimKind, lang: Language): string {
  if (kind === 'literacy') return getLiteracyBeat2Caregiver(lang);

  if (kind === 'breathing') {
    return lang === 'en'
      ? [
          "**Switch:** I'm the **child** now.",
          '',
          'I whisper: **The leaves are shushing in the breeze… I’m breathing slow with Pepe under the tree, and it feels peaceful.**',
          '',
          'Your turn: in **2–3 short sentences**, **notice** my words or calm body, **mirror** the feeling, **say what you appreciate** about my noticing, and add a **light tie** to trees, breath, or caring for the Earth.',
          '',
          '**Write or say** your response.',
        ].join('\n')
      : [
          '**Cambio:** ahora yo soy el **niño/a**.',
          '',
          'Susurro: **Las hojas susurran con la brisa… respiro despacio con Pepe bajo el árbol, y se siente en paz.**',
          '',
          'Tu parte: en **2–3 frases**, **nota** mis palabras o mi calma en el cuerpo, **refleja** el sentimiento, **di qué valoras** de que haya notado el momento y añade un **lazo ligero** con árboles, respiración o cuidar la Tierra.',
          '',
          '**Escribe o di** tu respuesta.',
        ].join('\n');
  }

  if (kind === 'draw') {
    return lang === 'en'
      ? [
          "**Switch:** I'm the **child** now.",
          '',
          'I shout: **Zigzag spark! I’m drawing lightning across the sky!**',
          '',
          'Your turn: in **2–3 short sentences**, **see** my marks or words, **match** my excitement **without** fixing the drawing, **name something you genuinely like** about the try, **connect** lightly to **sky, scraps, leaves**, or **what we already have** on the table—and **ask an open-ended question** to extend the imagination (like "What happens next?" or "Where does it go?").',
          '',
          '**Write or say** your response.',
        ].join('\n')
      : [
          '**Cambio:** ahora yo soy el **niño/a**.',
          '',
          'Grito: **¡Chispa en zigzag! ¡Dibujo un rayo en el cielo!**',
          '',
          'Tu parte: en **2–3 frases**, **mira** mis trazos o palabras, **acompaña** mi emoción **sin** corregir el dibujo, **di qué te gusta de verdad** del intento, **conecta** en ligero con **cielo, retazos, hojas** o **lo que ya tenemos** en la mesa—y **haz una pregunta abierta** para extender la imaginación (como "¿Qué pasa después?" o "¿A dónde va?").',
          '',
          '**Escribe o di** tu respuesta.',
        ].join('\n');
  }

  // express (Peke)
  return lang === 'en'
    ? [
        "**Switch:** I'm the **child** now.",
        '',
        'I point at the screen: **Look! Peke is happy and laughing!**',
        '',
        "Your turn: in **2–3 short sentences**, **name** what I noticed about Peke, **stay with** my observation, **say something you value** about me noticing feelings, and **connect** to caring for others—or **share how you feel too** (like 'I feel happy today, just like Peke').",
        '',
        '**Write or say** your response.',
      ].join('\n')
    : [
        '**Cambio:** ahora yo soy el **niño/a**.',
        '',
        'Señalo la pantalla: **¡Mira! ¡Peke está feliz y riéndose!**',
        '',
        'Tu parte: en **2–3 frases**, **nombra** lo que noté sobre Peke, **quédate** con mi observación, **di qué valoras** de que note sentimientos y **conecta** con cuidar a los demás—o **comparte cómo te sientes tú también** (como "Yo me siento feliz hoy, igual que Peke").',
        '',
        '**Escribe o di** tu respuesta.',
      ].join('\n');
}

/** Tie to app + next steps */
export function getSimulationBeat3Feedback(kind: GameSimKind, lang: Language): string {
  if (kind === 'literacy') return getLiteracyBeat3Feedback(lang);

  if (kind === 'breathing') {
    return lang === 'en'
      ? [
          '**Pepe Knows:** In the app, follow **Pepe’s** stories—**pause**, **breathe**, and stay in that same **notice—join—appreciate—connect** rhythm when your child reacts to the tree, wind, or feelings.',
          '',
          '**Sustainability:** Calm attention helps us notice **life**—trees, air, birds—so we choose **care** over rush.',
        ].join('\n')
      : [
          '**Pepe Sabe:** En la app, sigue las historias de **Pepe**—**pausa**, **respira** y mantén el mismo ritmo de **notar—acompañar—valorar—conectar** cuando tu niño/a reacciona al árbol, el viento o los sentimientos.',
          '',
          '**Sostenibilidad:** La atención tranquila nos ayuda a notar la **vida**—árboles, aire, pájaros—y elegir **cuidado** en lugar de prisa.',
        ].join('\n');
  }

  if (kind === 'draw') {
    return lang === 'en'
      ? [
          '**Creature Superpowers:** In the app, pick a **creature**, give it **powers**, and **draw**—keep that **notice—join—appreciate—bridge** rhythm with story and nature.',
          '',
          '**Going deeper:** After they draw, **extend the imagination** with open-ended questions—"What happens next?" "Where does this creature live?" "What else can it do?" These questions **fuel more creativity**, keep the **energy flowing**, and help the **child lead** the story. Let their imagination guide—you\'re there to **amplify, not direct**.',
          '',
          '**Sustainability:** One sheet, many worlds—**reuse** edges, **celebrate** imperfection as creative.',
        ].join('\n')
      : [
          '**Superpoderes:** En la app, elige una **criatura**, dale **poderes** y **dibuja**—mantén el ritmo **notar—acompañar—valorar—enlazar** con el cuento y la naturaleza.',
          '',
          '**Profundizar más:** Después de que dibujen, **extiende la imaginación** con preguntas abiertas—"¿Qué pasa después?" "¿Dónde vive esta criatura?" "¿Qué más puede hacer?" Estas preguntas **alimentan más creatividad**, mantienen la **energía fluyendo** y ayudan al **niño/a a liderar** la historia. Deja que su imaginación guíe—tú estás ahí para **amplificar, no dirigir**.',
          '',
          '**Sostenibilidad:** Una hoja, muchos mundos—**reutiliza** bordes y **celebra** lo imperfecto como creativo.',
        ].join('\n');
  }

  return lang === 'en'
    ? [
        "**Peke's Adventures:** In the app, you'll see Peke in different moments—**sleeping, sad, or happy and laughing**. Help your child notice and name what Peke is feeling. Use that same **notice—join—appreciate—connect** rhythm.",
        '',
        "**Going deeper:** After noticing Peke's feelings, **share your own**—'I feel happy today too' or 'Yesterday I felt sad like Peke.' The game becomes a bridge to talk about **how we're doing**, helping children understand that **all feelings are part of being human**. We can notice them, adapt to them, and know ourselves better.",
        '',
        '**Sustainability:** Noticing feelings in others builds **empathy**—a practice of caring for others, just like caring for living things in nature.',
      ].join('\n')
    : [
        '**Las aventuras de Peke:** En la app verás a Peke en distintos momentos—**durmiendo, triste o feliz y riéndose**. Ayuda a tu niño/a a notar y nombrar lo que siente Peke. Usa el mismo ritmo **notar—acompañar—valorar—conectar**.',
        '',
        "**Profundizar más:** Después de notar los sentimientos de Peke, **comparte los tuyos**—'Yo también me siento feliz hoy' o 'Ayer me sentí triste como Peke'. El juego se convierte en un puente para hablar de **cómo estamos**, ayudando a los niños/as a entender que **todos los sentimientos son parte de ser humano/a**. Podemos notarlos, adaptarnos a ellos y conocernos mejor.",
        '',
        '**Sostenibilidad:** Notar sentimientos en los demás cultiva **empatía**—un cuidado por los demás, como cuidar seres vivos en la naturaleza.',
      ].join('\n');
}

/** Readiness + CTA */
export function getSimulationBeat4Readiness(kind: GameSimKind, lang: Language): string {
  if (kind === 'literacy') return getLiteracyBeat4Readiness(lang);

  if (kind === 'breathing') {
    return lang === 'en'
      ? [
          '**Quick check:** How are you feeling about this path?',
          '',
          '**Open Pepe Knows** for a **short round** (2–3 minutes).',
          '',
          'Explore **stories**, choices, and calm moments with the tree.',
        ].join('\n')
      : [
          '**Comprobación rápida:** ¿Cómo te sientes con este camino?',
          '',
          '**Abre Pepe Sabe** para una **ronda corta** (2–3 minutos).',
          '',
          'Explora **historias**, decisiones y momentos tranquilos con el árbol.',
        ].join('\n');
  }

  if (kind === 'draw') {
    return lang === 'en'
      ? [
          '**Quick check:** Ready to draw imagination together?',
          '',
          '**Open Creature Superpowers** for a **short round** (2–3 minutes).',
          '',
          'Mix **creatures**, **powers**, and **drawing** in one playful flow.',
        ].join('\n')
      : [
          '**Comprobación rápida:** ¿Listo/a para dibujar imaginación juntos?',
          '',
          '**Abre Superpoderes** para una **ronda corta** (2–3 minutos).',
          '',
          'Mezcla **criaturas**, **poderes** y **dibujo** en un solo flujo lúdico.',
        ].join('\n');
  }

  return lang === 'en'
    ? [
        "**Quick check:** How did **noticing Peke's feelings** feel for you?",
        '',
        "**Open Peke's Adventures** for a **short round** (2–3 minutes).",
        '',
        "You'll see Peke **sleeping, sad, or happy**—help your child notice and name each moment.",
      ].join('\n')
    : [
        '**Comprobación rápida:** ¿Cómo te sentiste al **notar los sentimientos de Peke**?',
        '',
        '**Abre las Aventuras de Peke** para una **ronda corta** (2–3 minutos).',
        '',
        'Verás a Peke **durmiendo, triste o feliz**—ayuda a tu niño/a a notar y nombrar cada momento.',
      ].join('\n');
}

export type CaregiverAssessmentLevel = 'weak' | 'ok' | 'strong';

/** Closing copy after breathing micro-sim (replaces separate beat 3 + 4 blocks for this path). */
export function getBreathingPracticeDoneClosing(lang: Language): string {
  return lang === 'en'
    ? 'Thank you for practicing with me. **Calm attention** helps us notice **life**—trees, air, birds—so we choose **care** over rush. In **Pepe Knows**, stay in that gentle **notice—join—appreciate—bridge** rhythm when your child reacts to the **tree** and **shares feelings**.'
    : 'Gracias por practicar conmigo. La **atención tranquila** nos ayuda a notar la **vida**—árboles, aire, pájaros—y elegir **cuidado** en lugar de prisa. En **Pepe Sabe**, mantén ese ritmo suave de **notar—acompañar—valorar—enlazar** cuando tu niño/a reacciona al **árbol** y **comparte sentimientos**.';
}

/** Closing after draw micro-sim — WonderGuidance: child-led marks, adult scaffolds; four skills + reuse without lecturing. */
export function getDrawPracticeDoneClosing(lang: Language): string {
  return lang === 'en'
    ? 'Thank you for practicing with me. In **Creature Superpowers**, **their** creature, powers, and lines **lead**—you **join in** with **notice—join—appreciate—bridge** as you go, **without** taking the drawing away from them. **Reuse** edges and **paper you already have** when it fits; the app’s flow is built for that same rhythm.'
    : 'Gracias por practicar conmigo. En **Superpoderes**, **su** criatura, poderes y trazos **mandan**—tú **te sumas** con **notar—acompañar—valorar—enlazar** sobre la marcha, **sin** quitarles el dibujo. **Reutiliza** bordes y **papel que ya tengáis** cuando encaje; el flujo de la app sigue ese mismo ritmo.';
}

/** Closing after express / Peke micro-sim — noticing Peke's emotional states; adult scaffolds observation. */
export function getExpressPracticeDoneClosing(lang: Language): string {
  return lang === 'en'
    ? "Thank you for practicing with me. In **Peke's Adventures**, you'll see Peke in different moments—**sleeping, sad, or happy and laughing**. When your child notices and names what Peke is feeling, **stay in** that **notice—join—appreciate—connect** rhythm. **Celebrate** their observation without correcting or shrinking what they saw. This practice of noticing others' feelings builds **empathy** and care."
    : 'Gracias por practicar conmigo. En **las Aventuras de Peke**, verás a Peke en distintos momentos—**durmiendo, triste o feliz y riéndose**. Cuando tu niño/a note y nombre lo que siente Peke, **mantén** el ritmo **notar—acompañar—valorar—conectar**. **Celebra** su observación sin corregir o achicar lo que vio. Esta práctica de notar sentimientos ajenos cultiva **empatía** y cuidado.';
}

/**
 * Scripted coach feedback on the caregiver’s simulation reply (non-literacy).
 * Literacy uses `getLiteracyAfterCaregiverWrapUp` instead.
 */
export function getSimulationCaregiverReplyFeedback(
  kind: Exclude<GameSimKind, 'literacy'>,
  lang: Language,
  level: CaregiverAssessmentLevel
): string {
  if (kind === 'breathing') {
    if (lang === 'en') {
      if (level === 'strong') {
        return [
          '**Coaching feedback:** Your lines used **describe**, **reflect**, **praise**, and **relate** together, and you tied the moment to **breath, trees, or caring for life**—that matches how **Pepe** stories work in the app.',
        ].join('\n');
      }
      if (level === 'ok') {
        return [
          '**Coaching feedback:** You stayed with the child’s words and the calm thread. Next time, you can sharpen **relate** with something concrete—**air**, **leaves**, or a small **next step** you might try together.',
        ].join('\n');
      }
      return [
        '**Coaching feedback:** Showing up for the practice counts; calm with young children grows from **small, repeated moments**. **Pepe** in the app offers more **pauses and prompts** beside the tree.',
      ].join('\n');
    }
    if (level === 'strong') {
      return [
        '**Retroalimentación:** Tus frases combinan **describir**, **reflejar**, **elogiar** y **relacionar**, y enlazaste el momento con **respiración, árboles o cuidar la vida**—como en las historias de **Pepe** en la app.',
      ].join('\n');
    }
    if (level === 'ok') {
      return [
        '**Retroalimentación:** Te quedaste con las palabras del niño/a y el hilo tranquilo. La próxima vez puedes afilar **relacionar** con algo concreto—**aire**, **hojas** o un **siguiente paso** pequeño juntos.',
      ].join('\n');
    }
    return [
      '**Retroalimentación:** El intento cuenta; la calma con niños pequeños crece con **momentos chicos y repetidos**. **Pepe** en la app ofrece más **pausas e invitaciones** junto al árbol.',
    ].join('\n');
  }

  if (kind === 'draw') {
    if (lang === 'en') {
      if (level === 'strong') {
        return [
          '**Coaching feedback:** Your reply **named** what showed up in their play, **stayed with** the zip of their idea **without** grabbing the pencil, **lifted** a real part of the try, and **opened** a door to **sky, scraps, or reuse**—that’s how Wonderpath **backs their lead**. **Creature Superpowers** runs the same cadence step by step.',
        ].join('\n');
      }
      if (level === 'ok') {
        return [
          '**Coaching feedback:** You’re **tracking** their thread. If something felt thin, it’s often **relate**: one concrete nod—**clouds**, **a torn corner**, **what their creature does next**—keeps the story **theirs** while you stay beside them.',
        ].join('\n');
      }
      return [
        '**Coaching feedback:** **Being here** is already practice. **Superpowers** breaks drawing into **short beats** (creature → power → mark) so you can rehearse **notice, join, appreciate, bridge** in **small** doses—**child first**, you **scaffold**.',
      ].join('\n');
    }
    if (level === 'strong') {
      return [
        '**Retroalimentación:** Tu respuesta **nombró** lo que apareció en el juego, **siguió** el ritmo de su idea **sin** quedarte el lápiz, **valoró** algo real del intento y **abrió** un puente al **cielo, retazos o reutilizar**—así Wonderpath **refuerza** su liderazgo. **Superpoderes** repite ese mismo compás paso a paso.',
      ].join('\n');
    }
    if (level === 'ok') {
      return [
        '**Retroalimentación:** Vas **siguiendo** su hilo. Si algo sonó flojo, suele ser **relacionar**: un detalle concreto—**nubes**, **un borde de papel**, **qué hace su criatura después**—mantiene la historia **suya** mientras tú **acompañas**.',
      ].join('\n');
    }
    return [
      '**Retroalimentación:** **Estar aquí** ya es práctica. **Superpoderes** parte el dibujo en **momentos cortos** (criatura → poder → trazo) para ensayar **notar, acompañar, valorizar, enlazar** en **dosis pequeñas**—**el niño/a primero**, tú **apoyas**.',
    ].join('\n');
  }

  // express (Peke)
  if (kind === 'express') {
    if (lang === 'en') {
      if (level === 'strong') {
        return [
          '**Coaching feedback:** You **noticed** something real in their words or body, **stayed with** the burst **without** downsizing it, **praised** the honesty of the try, and **bridged** toward **home, kindness, or our shared world**—that **holds** their lead the way **Peke’s Adventures** are paced in the app.',
        ].join('\n');
      }
      if (level === 'ok') {
        return [
          '**Coaching feedback:** You’re **with** them. If something felt thin, try a clearer **relate**—**who matters**, **what happens next in play**, or **one gentle link to place**—so they feel **met**, not managed.',
        ].join('\n');
      }
      return [
        '**Coaching feedback:** **Being here** for big feelings is practice too. **Peke** breaks the path into **short story beats** so you can rehearse **notice, join, appreciate, bridge** in **small** steps—**their** feeling **first**, you **scaffold**.',
      ].join('\n');
    }
    if (level === 'strong') {
      return [
        '**Retroalimentación:** **Notaste** algo real en sus palabras o cuerpo, **te quedaste** con el estallido **sin** achicarlo, **elogiaste** la honestidad del intento y **enlazaste** hacia **hogar, bondad o nuestro mundo compartido**—así **sostienes** su liderazgo como ritman las **Aventuras de Peke** en la app.',
      ].join('\n');
    }
    if (level === 'ok') {
      return [
        '**Retroalimentación:** Estás **con** ellos. Si algo sonó flojo, prueba un **relacionar** más claro—**a quién le importa**, **qué sigue en el juego** o **un lazo suave al lugar**—para que se sientan **acompañados**, no administrados.',
      ].join('\n');
    }
    return [
      '**Retroalimentación:** **Estar aquí** con emociones grandes también es práctica. **Peke** parte el camino en **momentos cortos de historia** para ensayar **notar, acompañar, valorizar, enlazar** en **pasos chicos**—**su** sentimiento **primero**, tú **apoyas**.',
    ].join('\n');
  }

  const _exhaustive: never = kind;
  return _exhaustive;
}

/** Single bubble after caregiver practice: coaching feedback, app tie-in, open-game cue, handoff to cards. */
export function getSimulationCaregiverPhaseWrapUp(
  kind: GameSimKind,
  lang: Language,
  caregiverPracticeRaw: string,
  assessmentLevel: CaregiverAssessmentLevel
): string {
  if (kind === 'literacy') {
    return [
      getLiteracyAfterCaregiverWrapUp(lang, caregiverPracticeRaw),
      getLiteracyBeat3Feedback(lang),
      getLiteracyBeat4Readiness(lang),
    ].join('\n\n');
  }
  if (kind === 'breathing') {
    return [
      getSimulationCaregiverReplyFeedback(kind, lang, assessmentLevel),
      getBreathingPracticeDoneClosing(lang),
      getPlayTogetherPostSimHandoff(lang),
    ].join('\n\n');
  }
  if (kind === 'draw') {
    return [
      getSimulationCaregiverReplyFeedback(kind, lang, assessmentLevel),
      getDrawPracticeDoneClosing(lang),
      getPlayTogetherPostSimHandoff(lang),
    ].join('\n\n');
  }
  if (kind === 'express') {
    return [
      getSimulationCaregiverReplyFeedback(kind, lang, assessmentLevel),
      getExpressPracticeDoneClosing(lang),
      getPlayTogetherPostSimHandoff(lang),
    ].join('\n\n');
  }
  const _wrapExhaustive: never = kind;
  return _wrapExhaustive;
}

/** Constructive coaching when caregiver reply is thin */
export function getSimulationConstructiveCaregiverFeedback(kind: GameSimKind, lang: Language): string {
  if (kind === 'literacy') return getLiteracyConstructiveCaregiverFeedback(lang);

  if (kind === 'breathing') {
    return lang === 'en'
      ? [
          "Thanks for trying—that's how we learn.",
          '',
          '**Here\'s an example** for "The leaves are shushing... I\'m breathing slow...":',
          '"I hear you noticing the leaves and your calm breathing. Shushing leaves, slow breath—that peaceful feeling. I love that you paused to notice those quiet moments. When we pay attention to trees and our breath, we\'re caring for the living world and ourselves."',
          '',
          '**Try again in 2–3 short sentences.** Include:',
          '1️⃣ **What you notice** (leaves, breath, peaceful)',
          '2️⃣ **Mirror it back** ("shushing leaves...")',
          '3️⃣ **Praise the noticing** ("I love that you paused...")',
          '4️⃣ **Connect to nature care** (trees, air, Earth)',
          '',
          '**Send when ready.**',
        ].join('\n')
      : [
          'Gracias por intentarlo—así aprendemos.',
          '',
          '**Aquí un ejemplo** para "Las hojas susurran... respiro despacio...":',
          '"Escucho que notas las hojas y tu respiración tranquila. Hojas susurrando, respiración lenta—esa sensación de paz. Me encanta que te detuviste a notar esos momentos quietos. Cuando prestamos atención a los árboles y nuestra respiración, estamos cuidando el mundo vivo y a nosotros mismos."',
          '',
          '**Intenta otra vez en 2–3 frases.** Incluye:',
          '1️⃣ **Lo que notas** (hojas, respiración, paz)',
          '2️⃣ **Devuélvelo** ("hojas susurrando...")',
          '3️⃣ **Elogia el notar** ("Me encanta que te detuviste...")',
          '4️⃣ **Conecta con cuidar la naturaleza** (árboles, aire, Tierra)',
          '',
          '**Envía cuando quieras.**',
        ].join('\n');
  }

  if (kind === 'draw') {
    return lang === 'en'
      ? [
          "Thanks for trying—that's how we learn.",
          '',
          '**Here\'s an example** for "Zigzag spark! I\'m drawing lightning!":',
          '"I see that zigzag line cutting across! Lightning—zigzag! I love how bold and fast your line is. We can draw this lightning on the back of this paper we already have—reusing what we have helps our Earth."',
          '',
          '**Try again in 2–3 short sentences.** Include:',
          '1️⃣ **What you see** (the zigzag, the movement)',
          '2️⃣ **Mirror the energy** ("Lightning—zigzag!")',
          '3️⃣ **Praise something specific** (boldness, speed, creativity)',
          '4️⃣ **Connect to materials** (reusing paper, sky, nature)',
          '',
          '**Send when ready.**',
        ].join('\n')
      : [
          'Gracias por intentarlo—así aprendemos.',
          '',
          '**Aquí un ejemplo** para "¡Chispa en zigzag! ¡Dibujo un rayo!":',
          '"¡Veo esa línea en zigzag cruzando! Rayo—¡zigzag! Me encanta lo atrevida y rápida que es tu línea. Podemos dibujar este rayo en la parte de atrás de este papel que ya tenemos—reutilizar lo que tenemos ayuda a nuestra Tierra."',
          '',
          '**Intenta otra vez en 2–3 frases.** Incluye:',
          '1️⃣ **Lo que ves** (el zigzag, el movimiento)',
          '2️⃣ **Refleja la energía** ("¡Rayo—zigzag!")',
          '3️⃣ **Elogia algo específico** (atrevimiento, velocidad, creatividad)',
          '4️⃣ **Conecta con materiales** (reutilizar papel, cielo, naturaleza)',
          '',
          '**Envía cuando quieras.**',
        ].join('\n');
  }

  if (kind === 'express') {
    return lang === 'en'
      ? [
          "Thanks for trying—that's how we learn.",
          '',
          "**Here's an example** for \"Look! Peke is happy and laughing!\":",
          "\"You saw Peke happy and laughing! Look—Peke is really joyful! I love that you noticed that big smile and that laugh. When we notice how others feel, we're learning to care for people and animals around us.\"",
          '',
          '**Try again in 2–3 short sentences.** Include:',
          '1️⃣ **What you noticed** (Peke happy, laughing)',
          '2️⃣ **Mirror the observation** ("Peke is happy!")',
          '3️⃣ **Praise the noticing** ("I love that you saw...")',
          '4️⃣ **Connect to caring** (empathy, kindness to others)',
          '',
          '**Send when ready.**',
        ].join('\n')
      : [
          'Gracias por intentarlo—así aprendemos.',
          '',
          '**Aquí hay un ejemplo** para "¡Mira! ¡Peke está feliz y riendo!":',
          '"¡Viste a Peke feliz y riendo! Mira—Peke está muy alegre. Me encanta que notaste esa sonrisa grande y esa risa. Cuando notamos cómo se sienten otros, estamos aprendiendo a cuidar de personas y animales a nuestro alrededor."',
          '',
          '**Intenta otra vez en 2–3 frases.** Incluye:',
          '1️⃣ **Lo que notaste** (Peke feliz, riendo)',
          '2️⃣ **Refleja la observación** ("¡Peke está feliz!")',
          '3️⃣ **Elogia el notar** ("Me encanta que viste...")',
          '4️⃣ **Conecta con cuidar** (empatía, bondad hacia otros)',
          '',
          '**Envía cuando quieras.**',
        ].join('\n');
  }

  const _never: never = kind;
  return _never;
}

/** Tighter second-round nudge (after first coaching message) */
export function getSimulationConstructiveCaregiverFeedbackRound2(kind: GameSimKind, lang: Language): string {
  if (kind === 'literacy') return getLiteracyConstructiveCaregiverFeedbackRound2(lang);

  if (kind === 'breathing') {
    return lang === 'en'
      ? [
          '**Coaching feedback:** One more pass as the caregiver. Let\'s build it step by step:',
          '',
          '**Start with:** "I hear..." or "I notice..." (leaves, breath, peaceful)',
          '**Then add:** "Shushing leaves, slow breath..." (mirror the calm)',
          '**Then praise:** "I love that you..." (pausing to notice)',
          '**End with:** "When we pay attention..." (trees, air, Earth)',
          '',
          '**Example:** "I hear you noticing the leaves and your calm breathing. Shushing leaves, slow breath—that peaceful feeling. I love that you paused for those quiet moments. When we pay attention to trees and our breath, we\'re caring for the living world and ourselves."',
          '',
          '**Your turn—send 2–3 sentences when ready.**',
        ].join('\n')
      : [
          '**Retroalimentación de coaching:** Un pase más como cuidador/a. Construyámoslo paso a paso:',
          '',
          '**Comienza con:** "Escucho..." o "Noto..." (hojas, respiración, paz)',
          '**Luego añade:** "Hojas susurrando, respiración lenta..." (refleja la calma)',
          '**Luego elogia:** "Me encanta que..." (pausar para notar)',
          '**Termina con:** "Cuando prestamos atención..." (árboles, aire, Tierra)',
          '',
          '**Ejemplo:** "Escucho que notas las hojas y tu respiración calmada. Hojas susurrando, respiración lenta—ese sentimiento de paz. Me encanta que pausaste para esos momentos tranquilos. Cuando prestamos atención a los árboles y nuestra respiración, estamos cuidando del mundo viviente y de nosotros mismos."',
          '',
          '**Tu turno—envía 2–3 frases cuando quieras.**',
        ].join('\n');
  }

  if (kind === 'draw') {
    return lang === 'en'
      ? [
          '**Coaching feedback:** One more pass as the caregiver. Let\'s build it step by step:',
          '',
          '**Start with:** "I see..." or "I notice..." (the zigzag, the movement)',
          '**Then add:** "Lightning—zigzag!" (mirror the energy)',
          '**Then praise:** "I love how..." (boldness, speed, creativity)',
          '**End with:** "We can draw this..." (reusing paper, caring for Earth)',
          '',
          '**Example:** "I see that zigzag line cutting across! Lightning—zigzag! I love how bold and fast your line is. We can draw this lightning on the back of paper we already have—reusing what we have helps our Earth."',
          '',
          '**Your turn—send 2–3 sentences when ready.**',
        ].join('\n')
      : [
          '**Retroalimentación de coaching:** Un pase más como cuidador/a. Construyámoslo paso a paso:',
          '',
          '**Comienza con:** "Veo..." o "Noto..." (el zigzag, el movimiento)',
          '**Luego añade:** "¡Relámpago—zigzag!" (refleja la energía)',
          '**Luego elogia:** "Me encanta cómo..." (audacia, velocidad, creatividad)',
          '**Termina con:** "Podemos dibujar esto..." (reutilizar papel, cuidar la Tierra)',
          '',
          '**Ejemplo:** "¡Veo esa línea en zigzag cortando a través! ¡Relámpago—zigzag! Me encanta cuán audaz y rápida es tu línea. Podemos dibujar este relámpago en el reverso de papel que ya tenemos—reutilizar lo que tenemos ayuda a nuestra Tierra."',
          '',
          '**Tu turno—envía 2–3 frases cuando quieras.**',
        ].join('\n');
  }

  if (kind === 'express') {
    return lang === 'en'
      ? [
          '**Coaching feedback:** One more pass as the caregiver. Let\'s build it step by step:',
          '',
          '**Start with:** "You saw..." or "You noticed..." (what they observed)',
          '**Then add:** "Peke is happy!" (mirror their observation)',
          '**Then praise:** "I love that you..." (noticing feelings)',
          '**End with:** "When we notice..." (empathy and caring for others)',
          '',
          '**Example:** "You saw Peke happy and laughing! Look—Peke is really joyful! I love that you\'re noticing those big emotions. When we pay attention to how others feel, we\'re practicing kindness and care."',
          '',
          '**Your turn—send 2–3 sentences when ready.**',
        ].join('\n')
      : [
          '**Retroalimentación de coaching:** Un pase más como cuidador/a. Construyámoslo paso a paso:',
          '',
          '**Comienza con:** "Viste..." o "Notaste..." (lo que observaron)',
          '**Luego añade:** "¡Peke está feliz!" (refleja su observación)',
          '**Luego elogia:** "Me encanta que..." (notar sentimientos)',
          '**Termina con:** "Cuando notamos..." (empatía y cuidar a otros)',
          '',
          '**Ejemplo:** "¡Viste a Peke feliz y riendo! Mira—Peke está muy alegre. Me encanta que estés notando esas grandes emociones. Cuando prestamos atención a cómo se sienten otros, estamos practicando bondad y cuidado."',
          '',
          '**Tu turno—envía 2–3 frases cuando quieras.**',
        ].join('\n');
  }

  const _neverR2: never = kind;
  return _neverR2;
}

/** Shown once when we advance after max coaching rounds while the reply still scores weak */
export function getSimulationCaregiverSoftLanding(kind: GameSimKind, lang: Language): string {
  if (kind === 'literacy') return getLiteracyCaregiverSoftLanding(lang);

  if (kind === 'breathing') {
    return lang === 'en'
      ? [
          '**We’ll move on together**—mindfulness with kids is practice, not perfection.',
          '',
          'Here’s how **Pepe** and the tree meet you in the app:',
        ].join('\n')
      : [
          '**Seguimos juntos/as**—la atención plena con niños es práctica, no perfección.',
          '',
          'Así te recibe **Pepe** y el árbol en la app:',
        ].join('\n');
  }

  if (kind === 'draw') {
    return lang === 'en'
      ? [
          '**We’ll move on together**—wobbly lines and loud ideas still count; you’re **scaffolding**, not scoring.',
          '',
          'Here’s how **Creature Superpowers** carries the same rhythm in the app:',
        ].join('\n')
      : [
          '**Seguimos juntos/as**—líneas torcidas e ideas fuertes cuentan; estás **apoyando**, no poniendo nota.',
          '',
          'Así **Superpoderes** lleva el mismo ritmo en la app:',
        ].join('\n');
  }

  if (kind === 'express') {
    return lang === 'en'
      ? [
          '**We’ll move on together**—big feelings land messy sometimes; you’re **scaffolding**, not solving or scoring.',
          '',
          'Here’s how **Peke’s Adventures** carries the same **notice—join—appreciate—bridge** rhythm in the app:',
        ].join('\n')
      : [
          '**Seguimos juntos/as**—las emociones grandes aterrizan revueltas a veces; estás **apoyando**, no resolviendo ni poniendo nota.',
          '',
          'Así **las Aventuras de Peke** llevan el mismo ritmo **notar—acompañar—valorizar—enlazar** en la app:',
        ].join('\n');
  }

  const _neverSoft: never = kind;
  return _neverSoft;
}
